import React, { useState } from 'react';
import {
  Box,
  Text,
  HStack,
  VStack,
  Button,
  ButtonText,
  ButtonIcon,
  Pressable,
  Icon,
  Select,
  SelectTrigger,
  SelectInput,
  SelectPortal,
  SelectBackdrop,
  SelectContent,
  SelectDragIndicatorWrapper,
  SelectDragIndicator,
  SelectItem,
  Tooltip,
  TooltipContent,
  TooltipText,
  Spinner,
} from '@gluestack-ui/themed';
import { useFormContext, useWatch, Controller } from 'react-hook-form';
import { RotateCcw, Send, Save } from 'lucide-react-native';
import { useTranslation } from 'react-i18next';
import { useEvaluationGradingConfig } from '@/lecturer/EvaluationDesk/context/EvaluationGradingConfigContext';
import GradeCard from '@/common/components/GradeCard';
import SimpleRTE from '@/common/components/forms/SimpleRTE';
import { taskRequiresEvaluation } from '@/common/utils/taskEvaluationUtils';
import log from '@/common/utils/logger';
import GenerateWithAIBadge from '@/common/components/GenerateWithAIBadge';
import { useUserFeatures } from '@/common/hooks/useUserFeatures';
import { useDialog } from '@/common/context/DialogContext';
import { MODULE_GRADE_OPTIONS } from '@/common/grading/gradeOptions';
import { handleComputeGrade, aggregateTaskGrades } from '../utils/moduleGrading';
import { getEvaluationStats } from '../utils/gradingConversions';
import { isOralExamTask } from '../utils/evaluationFormMapping';

const GRADE_OPTIONS = MODULE_GRADE_OPTIONS;

const GradingForm = ({
  tasks, eventKinds, isDisabled, isReadOnly, isRegenerating, onRegenerate, onSave, onSend, moduleSubmissionId,
}) => {
  const { setValue, control } = useFormContext();
  const { t } = useTranslation();
  const { showToastMessage } = useDialog();
  const showToastMessageRef = React.useRef(showToastMessage);
  showToastMessageRef.current = showToastMessage;
  // Keep auto-compute effect deps stable if `t` identity churns (e.g. test mocks).
  const tRef = React.useRef(t);
  tRef.current = t;
  const { isFeatureActive } = useUserFeatures();
  const taskAiEnabled = isFeatureActive('lecturer-task-ai');
  const { levelWeights, dimensionMaxWeights, gradeProjector } = useEvaluationGradingConfig();
  const [isUpdatingGrade, setIsUpdatingGrade] = useState(false);
  // Tracks the serialized evaluation state used for the last grade calculation.
  // Prevents re-triggering when a server refetch resets form state to the same values.
  const lastCalcEvalRef = React.useRef(null);

  const taskEvaluations = useWatch({ name: 'taskEvaluations' });
  const taskEvaluationsRef = React.useRef(taskEvaluations);
  taskEvaluationsRef.current = taskEvaluations;
  const autoCalculateGrade = useWatch({ name: 'grading.autoCalculateGrade', defaultValue: true });
  const currentGrade = useWatch({ name: 'grading.grade' });
  const hasSuppressedTaskType = tasks?.some(
    (task) => isOralExamTask(task) || task.type === 'multiple_choice',
  ) ?? false;

  // Calculate grade breakdown for tooltip
  const calculateGradeBreakdown = React.useCallback(() => {
    if (!taskEvaluations || !tasks || tasks.length === 0) {
      return null;
    }

    try {
      const evaluationsByType = {
        full: tasks
          .filter((task) => taskRequiresEvaluation(task, eventKinds)
            && task.gradingSystem === 'full_evaluation'
            && !isOralExamTask(task))
          .map((task) => taskEvaluations[task.id]?.evaluation?.dimensions || {}),
        passFail: tasks
          .filter((task) => taskRequiresEvaluation(task, eventKinds)
            && task.gradingSystem === 'pass_fail'
            && !isOralExamTask(task))
          .map((task) => taskEvaluations[task.id]?.evaluation?.dimensions || {}),
      };

      const fullEvalStats = getEvaluationStats(evaluationsByType.full);
      const passFailStats = getEvaluationStats(evaluationsByType.passFail);

      const fullEvalGrade = fullEvalStats.evaluated > 0
        ? aggregateTaskGrades(evaluationsByType.full, levelWeights, dimensionMaxWeights, gradeProjector)
        : null;

      const passFailGrade = passFailStats.evaluated > 0
        ? aggregateTaskGrades(evaluationsByType.passFail, levelWeights, dimensionMaxWeights, gradeProjector)
        : null;

      const parts = [];
      if (fullEvalGrade !== null && !Number.isNaN(fullEvalGrade)) {
        parts.push(`${fullEvalGrade.toFixed(1)} transfer`);
      }
      if (passFailGrade !== null && !Number.isNaN(passFailGrade)) {
        parts.push(`${passFailGrade.toFixed(1)} regular`);
      }

      return parts.length > 0 ? parts.join(' / ') : null;
    } catch (error) {
      log.warn('Error calculating grade breakdown:', error);
      return null;
    }
  }, [taskEvaluations, tasks, levelWeights, dimensionMaxWeights, eventKinds, gradeProjector]);

  const gradeBreakdown = calculateGradeBreakdown();

  const runAutoCompute = React.useCallback(async (evalSnapshot) => {
    const silentSetValue = (path, value, options) => {
      setValue(path, value, { ...options, shouldDirty: false });
    };

    try {
      const result = await handleComputeGrade({
        tasks,
        formData: taskEvaluationsRef.current,
        setValue: silentSetValue,
        showDialog: () => {},
        showToastMessage: (type, message, title) => {
          if (type === 'error') {
            showToastMessageRef.current('error', message, title);
          }
        },
        eventKinds,
        moduleSubmissionId,
      });

      if (result?.ok && result.grade) {
        lastCalcEvalRef.current = evalSnapshot;
      }
    } catch (error) {
      log.warn('Auto-grade calculation failed:', error);
      showToastMessageRef.current('error', tRef.current('lecturer.evalDesk.autoGradeComputeError'));
    } finally {
      setIsUpdatingGrade(false);
    }
  }, [tasks, setValue, eventKinds, moduleSubmissionId]);

  // Stable dep for evaluation content (avoid useWatch reference churn + inline JSON.stringify in deps).
  const evalSnapshotDep = taskEvaluations ? JSON.stringify(taskEvaluations) : null;

  // Auto-calculate grade when task evaluations change (debounced)
  React.useEffect(() => {
    if (!autoCalculateGrade || !evalSnapshotDep || isReadOnly || isDisabled) {
      setIsUpdatingGrade(false);
      return undefined;
    }

    // Check synchronously before setting any visual state: if the evaluation
    // data matches the last calculation, this is a server-refetch re-trigger
    // and we can skip entirely with no gray-out.
    if (evalSnapshotDep === lastCalcEvalRef.current) {
      return undefined;
    }

    setIsUpdatingGrade(true);

    const isFirstCompute = lastCalcEvalRef.current === null;
    const delayMs = isFirstCompute ? 100 : 2000;

    const timeoutId = setTimeout(() => {
      // Re-check after debounce in case another effect fire updated the ref.
      if (evalSnapshotDep === lastCalcEvalRef.current) {
        setIsUpdatingGrade(false);
        return;
      }

      runAutoCompute(evalSnapshotDep);
    }, delayMs);

    return () => {
      clearTimeout(timeoutId);
      setIsUpdatingGrade(false);
    };
  }, [
    evalSnapshotDep,
    autoCalculateGrade,
    isReadOnly,
    isDisabled,
    runAutoCompute,
  ]);

  const enableAutoGrade = () => {
    setValue('grading.autoCalculateGrade', true, { shouldDirty: true });
    if (taskEvaluations && !isReadOnly && !isDisabled) {
      const evalSnapshot = JSON.stringify(taskEvaluations);
      setIsUpdatingGrade(true);
      runAutoCompute(evalSnapshot);
    }
  };

  const disableAutoGrade = () => {
    setValue('grading.autoCalculateGrade', false, { shouldDirty: true });
  };

  const isGradePending = autoCalculateGrade && (isUpdatingGrade || !currentGrade || currentGrade === '');

  const guardPendingGrade = () => {
    if (!isGradePending) {
      return false;
    }
    showToastMessage('error', t('lecturer.evalDesk.autoGradePendingSave'));
    return true;
  };

  const handleSaveClick = () => {
    if (guardPendingGrade()) return;
    onSave();
  };

  const handleSendClick = () => {
    if (guardPendingGrade()) return;
    onSend();
  };

  return (
    <VStack space="md" maxWidth={1400} mt="$6">
      {/* Section heading */}
      <Text
        size="xs"
        color="$textSubtle"
        fontWeight="bold"
        textTransform="uppercase"
        letterSpacing="$sm"
      >
        Module Grade & Feedback
      </Text>

      {/* Content with left accent bar - Grade + Feedback only */}
      <Box borderLeftWidth={3} borderLeftColor="$brandBase" pl="$4">
        <HStack alignItems="stretch" space="lg">
          {/* Grade Section */}
          <VStack alignItems="center" justifyContent="center" w={80} flexShrink={0}>
            <Box position="absolute" w={1} h={1} overflow="hidden" opacity={0}>
              <Text testID="grading-grade-display">{currentGrade}</Text>
            </Box>
            {(autoCalculateGrade || isReadOnly) ? (
              // Auto mode or finalized record - show compact grade card
              <>
                {isUpdatingGrade && !isReadOnly ? (
                  <Box w={60} h={60} alignItems="center" justifyContent="center">
                    <Spinner size="small" />
                  </Box>
                ) : (
                  <Box opacity={isUpdatingGrade ? 0.4 : 1}>
                    {currentGrade && currentGrade !== '' ? (
                      <GradeCard grade={currentGrade} size="sm" compact tooltip={gradeBreakdown} />
                    ) : (
                      <Box w={60} h={60} alignItems="center" justifyContent="center">
                        <Text size="xs" color="$textHint">--</Text>
                      </Box>
                    )}
                  </Box>
                )}
                {!isReadOnly && (
                  <Pressable onPress={disableAutoGrade} mt="$1" opacity={isUpdatingGrade ? 0.5 : 1}>
                    <Text size="2xs" color="$textHint">
                      Override
                    </Text>
                  </Pressable>
                )}
              </>
            ) : (
              // Manual mode - show dropdown
              <>
                <Controller
                  name="grading.grade"
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <Select selectedValue={value} onValueChange={onChange} isDisabled={isReadOnly || isDisabled}>
                      <SelectTrigger variant="outline" size="sm" borderColor="$borderLight" w={65} h={36}>
                        <SelectInput placeholder="Grade" fontSize={13} fontWeight="semibold" textAlign="center" />
                      </SelectTrigger>
                      <SelectPortal>
                        <SelectBackdrop />
                        <SelectContent maxHeight={300}>
                          <SelectDragIndicatorWrapper>
                            <SelectDragIndicator />
                          </SelectDragIndicatorWrapper>
                          {GRADE_OPTIONS.map((option) => (
                            <SelectItem key={option.value} label={option.label} value={option.value} />
                          ))}
                        </SelectContent>
                      </SelectPortal>
                    </Select>
                  )}
                />
                {!isReadOnly && (
                  <Pressable onPress={enableAutoGrade} mt="$1">
                    <HStack alignItems="center" space="xs">
                      <Icon as={RotateCcw} size="3xs" color="$textHint" />
                      <Text size="2xs" color="$textHint">
                        Auto
                      </Text>
                    </HStack>
                  </Pressable>
                )}
              </>
            )}
          </VStack>

          {!hasSuppressedTaskType && (
            <Box flex={1}>
              <SimpleRTE
                name="grading.feedback"
                placeholder="Module feedback for the student..."
                readonly={isReadOnly}
                height={80}
                rules={{ required: 'Feedback is required' }}
              />
              {taskAiEnabled && !isReadOnly && onRegenerate && (
                <HStack justifyContent="flex-end" mt="$1">
                  <GenerateWithAIBadge
                    testID="grading-regenerate-button"
                    onGenerate={onRegenerate}
                    isStreaming={isRegenerating}
                    enabled={!isDisabled}
                    label={t('lecturer.evalDesk.regenerateButton')}
                  />
                </HStack>
              )}
            </Box>
          )}
        </HStack>
      </Box>

      {/* Action Buttons - Outside accent bar, right-aligned */}
      {!isReadOnly && onSave && onSend && (
        <HStack justifyContent="flex-end" space="md" pt="$2">
          {/* Secondary: Save without submitting */}
          <Tooltip
            placement="top"
            trigger={(triggerProps) => (
              <Button
                {...triggerProps}
                size="sm"
                variant="outline"
                isDisabled={isDisabled || isGradePending}
                onPress={handleSaveClick}
                testID="grading-save-button"
              >
                <ButtonIcon as={Save} size="xs" color="$textSubtle" mr="$1" />
                <ButtonText size="xs" color="$textSubtle">Save</ButtonText>
              </Button>
            )}
          >
            <TooltipContent bg="$backgroundDark800" px="$3" py="$2" maxWidth={280}>
              <TooltipText>
                <Text color="$white" size="xs">
                  Save your work without sending to the student
                </Text>
              </TooltipText>
            </TooltipContent>
          </Tooltip>

          {/* Primary: Submit to student */}
          <Tooltip
            placement="top"
            trigger={(triggerProps) => (
              <Button
                {...triggerProps}
                size="sm"
                action="primary"
                isDisabled={isDisabled || isGradePending}
                onPress={handleSendClick}
                testID="grading-send-button"
              >
                <ButtonIcon as={Send} size="xs" color="$white" mr="$1" />
                <ButtonText size="xs" color="$white">
                  Save & Submit to Student
                </ButtonText>
              </Button>
            )}
          >
            <TooltipContent bg="$backgroundDark800" px="$3" py="$2" maxWidth={280}>
              <TooltipText>
                <Text color="$white" size="xs">
                  Finalize and release evaluation to student
                </Text>
              </TooltipText>
            </TooltipContent>
          </Tooltip>
        </HStack>
      )}
    </VStack>
  );
};

export default GradingForm;
